/**
 * Vista cliente (TV): pantalla completa, datos superpuestos sobre la imagen.
 * Sincronización con el mando embebido: BroadcastChannel + ?canal=
 * ?mando=1 queda obsoleto (el mando está en el CRM).
 */

import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  CHANNEL_PREFIX,
  elegirPantallaTv,
  resolveFotoUrl,
  usePresentacionChannel,
} from '../presentacion/presentacionClienteShared';
import { usePresentacionPropiedadData } from '../presentacion/usePresentacionPropiedadData';
import { resolveVideoForPresentacion, videosPlayables } from '../presentacion/presentacionVideo';

function presentacionEstaEnIframeShell() {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Pantalla completa en el monitor TV (no primario) si el navegador lo permite
 * (Chrome/Edge: permiso «Gestionar ventanas en todas las pantallas» + opción `screen` en fullscreen).
 * Si no, fullscreen en la pantalla donde está la ventana.
 */
async function entrarFullscreenPantallaTv() {
  if (typeof document === 'undefined' || document.fullscreenElement) return true;
  const el = document.documentElement;
  const req =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  if (!req) return false;

  const tryReq = async (opts) => {
    try {
      if (opts) await req.call(el, opts);
      else await req.call(el);
      return !!document.fullscreenElement;
    } catch {
      if (opts) {
        try {
          await req.call(el);
          return !!document.fullscreenElement;
        } catch {
          return false;
        }
      }
      return false;
    }
  };

  try {
    if (typeof window.getScreenDetails === 'function') {
      const sd = await window.getScreenDetails();
      const screens = Array.from(sd.screens);
      const target = elegirPantallaTv(screens, sd.currentScreen);
      if (target) {
        const ok = await tryReq({ navigationUI: 'hide', screen: target });
        if (ok) return true;
      }
    }
  } catch {
    /* sin permiso o API no disponible */
  }

  return tryReq({ navigationUI: 'hide' });
}

function usePresentacionTvFullscreen() {
  const enIframeShell = useMemo(() => presentacionEstaEnIframeShell(), []);
  const [pistaFullscreen, setPistaFullscreen] = useState(false);

  const intentar = useCallback(() => {
    if (enIframeShell) return;
    void entrarFullscreenPantallaTv();
  }, [enIframeShell]);

  useEffect(() => {
    if (enIframeShell) {
      setPistaFullscreen(false);
      return;
    }
    const delays = [0, 50, 120, 280, 600, 1200, 2200];
    const timers = delays.map((ms) => setTimeout(() => void entrarFullscreenPantallaTv(), ms));
    return () => timers.forEach(clearTimeout);
  }, [enIframeShell]);

  useEffect(() => {
    const onFs = () => {
      if (enIframeShell) {
        setPistaFullscreen(false);
        return;
      }
      setPistaFullscreen(!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFs);
    onFs();
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, [enIframeShell]);

  useEffect(() => {
    if (enIframeShell) return undefined;
    const onPointer = () => {
      if (!document.fullscreenElement) void entrarFullscreenPantallaTv();
    };
    window.addEventListener('pointerdown', onPointer, { capture: true });
    return () => window.removeEventListener('pointerdown', onPointer, { capture: true });
  }, [enIframeShell]);

  useEffect(() => {
    if (enIframeShell) return undefined;
    const onKey = (e) => {
      if (document.fullscreenElement) return;
      if (e.key === 'Enter' || e.key.toLowerCase() === 'f') {
        e.preventDefault();
        void entrarFullscreenPantallaTv();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enIframeShell]);

  useEffect(() => {
    if (enIframeShell) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible' && !document.fullscreenElement) {
        void entrarFullscreenPantallaTv();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [enIframeShell]);

  return { pistaFullscreen, intentar };
}

function BarraDatosCliente({ referencia, precioTexto, hab, ban, m2, compact }) {
  const rowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: compact ? 8 : 14,
    fontSize: compact ? 13 : 20,
    fontWeight: 600,
    color: '#fff',
    textShadow: '0 1px 4px rgba(0,0,0,0.9)',
  };
  return (
    <div style={rowStyle}>
      {referencia && (
        <span>
          Ref. <strong>{referencia}</strong>
        </span>
      )}
      {precioTexto && <span>💰 {precioTexto}</span>}
      {hab != null && hab > 0 && <span>🛏 {hab} hab</span>}
      {ban != null && ban > 0 && <span>🚿 {ban}</span>}
      {m2 != null && m2 > 0 && <span>📐 {Math.round(m2)} m²</span>}
    </div>
  );
}

function VistaPantalla({ fotos, videos, index, setIndex, meta, canal }) {
  const { pistaFullscreen, intentar: intentarFullscreen } = usePresentacionTvFullscreen();

  const cerrarVisorPorMando = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && window.top && window.top !== window.self) {
        window.top.close();
        return;
      }
    } catch {
      /* cross-origin top: ignorar */
    }
    try {
      window.close();
    } catch {
      /* */
    }
  }, []);

  const urls = fotos.map((f) => resolveFotoUrl(f)).filter(Boolean);
  const safeIndex = urls.length ? Math.min(index, urls.length - 1) : 0;
  const url = urls[safeIndex];

  const listaVideos = videosPlayables(videos);
  const [modoVideo, setModoVideo] = useState(false);
  const [videoIndex, setVideoIndex] = useState(0);
  const safeVidIdx = listaVideos.length ? Math.min(videoIndex, listaVideos.length - 1) : 0;
  const filaVideo = listaVideos[safeVidIdx];
  const resolvedVideo = filaVideo ? resolveVideoForPresentacion(filaVideo) : null;

  const onGoto = useCallback(
    (i) => {
      setModoVideo(false);
      if (!urls.length) return;
      const n = Math.max(0, Math.min(i, urls.length - 1));
      setIndex(n);
    },
    [urls.length, setIndex]
  );

  const onVideoMode = useCallback(
    (msg) => {
      if (msg.show) {
        setModoVideo(true);
        if (typeof msg.videoIndex === 'number' && listaVideos.length > 0) {
          setVideoIndex(Math.max(0, Math.min(msg.videoIndex, listaVideos.length - 1)));
        }
      } else {
        setModoVideo(false);
      }
    },
    [listaVideos.length]
  );

  usePresentacionChannel(canal, onGoto, onVideoMode, cerrarVisorPorMando);

  useEffect(() => {
    if (!canal) return undefined;
    const ch = new BroadcastChannel(CHANNEL_PREFIX + canal);
    const bump = (delta) => {
      setIndex((i) => {
        const max = Math.max(0, urls.length - 1);
        const n = Math.max(0, Math.min(max, i + delta));
        try {
          ch.postMessage({ type: 'goto', index: n });
        } catch {
          /* ignorar */
        }
        return n;
      });
    };
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') bump(1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') bump(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      ch.close();
    };
  }, [canal, urls.length, setIndex]);

  useEffect(() => {
    if (listaVideos.length === 0) {
      setModoVideo(false);
      setVideoIndex(0);
      return;
    }
    if (videoIndex >= listaVideos.length) {
      setVideoIndex(listaVideos.length - 1);
    }
  }, [listaVideos.length, videoIndex]);

  const precioTexto =
    meta.precio_web_consultar
      ? 'Consultar'
      : meta.precio
        ? `${Number(meta.precio).toLocaleString('es-ES')} €`
        : '';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      {pistaFullscreen && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 14px',
            borderRadius: 10,
            background: 'rgba(15,23,42,0.92)',
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            maxWidth: 'min(420px, calc(100vw - 24px))',
            pointerEvents: 'auto',
          }}
        >
          <span style={{ fontSize: 12, color: '#e2e8f0', lineHeight: 1.35 }}>
            Pulsa aquí o en la imagen para <strong>pantalla completa</strong> (sin barra de direcciones). O tecla <strong>F</strong>.
          </span>
          <button
            type="button"
            onClick={() => void intentarFullscreen()}
            style={{
              flexShrink: 0,
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid #38bdf8',
              background: 'rgba(56,189,248,0.2)',
              color: '#e0f2fe',
              fontWeight: 700,
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Pantalla completa
          </button>
        </div>
      )}

      <div style={{ position: 'absolute', inset: 0 }}>
        {modoVideo && resolvedVideo ? (
          resolvedVideo.kind === 'youtube' ? (
            <iframe
              title="Vídeo"
              src={resolvedVideo.embedUrl}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : (
            <video
              key={resolvedVideo.src}
              src={resolvedVideo.src}
              controls
              playsInline
              autoPlay
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: '#000',
              }}
            />
          )
        ) : url ? (
          <img
            src={url}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              opacity: 0.7,
            }}
          >
            Sin fotos disponibles
          </div>
        )}
      </div>

      {modoVideo && listaVideos.length > 1 && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 16,
            fontSize: 12,
            fontWeight: 600,
            color: '#fff',
            textShadow: '0 1px 4px rgba(0,0,0,0.9)',
            opacity: 0.9,
            pointerEvents: 'none',
          }}
        >
          Vídeo {safeVidIdx + 1} / {listaVideos.length}
        </div>
      )}

      {!modoVideo && urls.length > 1 && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 16,
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
            textShadow: '0 1px 4px rgba(0,0,0,0.9)',
            opacity: 0.85,
            pointerEvents: 'none',
          }}
        >
          {safeIndex + 1} / {urls.length}
        </div>
      )}

      {!modoVideo && (
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '20px 16px 28px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)',
          pointerEvents: 'none',
        }}
      >
        <BarraDatosCliente
          referencia={meta.referencia}
          precioTexto={precioTexto}
          hab={meta.num_dormitorios}
          ban={meta.num_banos}
          m2={meta.m2_construidos}
        />
      </div>
      )}
    </div>
  );
}

export default function PresentacionCliente() {
  const { propiedadId } = useParams();
  const [searchParams] = useSearchParams();
  const canal = searchParams.get('canal') || '';
  const esMandoObsoleto = searchParams.get('mando') === '1';

  const { fotos, videos, meta, cargando, error } = usePresentacionPropiedadData(propiedadId || null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [propiedadId]);

  if (esMandoObsoleto) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f172a',
          color: '#e2e8f0',
          padding: 24,
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <p style={{ fontSize: 18, fontWeight: 700, margin: '0 0 12px' }}>Mando integrado en el CRM</p>
          <p style={{ fontSize: 14, opacity: 0.9, margin: 0, lineHeight: 1.5 }}>
            El control de la presentación ya no se abre en una ventana aparte. Cierra esta pestaña y usa el panel{' '}
            <strong>Vista cliente</strong>
            {' '}en la esquina del CRM, o vuelve a pulsar el icono de la TV en una propiedad.
          </p>
        </div>
      </div>
    );
  }

  if (!canal) {
    return (
      <div style={{ padding: 24, color: '#fff', background: '#111', minHeight: '100vh' }}>
        <p>Abre la presentación desde la ficha del cliente con el icono de la TV.</p>
      </div>
    );
  }

  if (cargando) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0a',
          color: '#94a3b8',
        }}
      >
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: '#fecaca', background: '#111', minHeight: '100vh' }}>
        {error}
      </div>
    );
  }

  return (
    <VistaPantalla
      fotos={fotos}
      videos={videos}
      index={index}
      setIndex={setIndex}
      meta={meta}
      canal={canal}
    />
  );
}
